// =====================================================================
// MisExpenses.jsx — Per-property MIS Expense Tracker shell
// =====================================================================
// Owns: property fetch + selection state + URL sync. The detail panel,
// AOP variance table, and AI ask box live in dedicated sibling components
// (see iter-35e split). Keeps this file as a thin orchestrator.
// =====================================================================

import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { Input } from "../../components/ui/input";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { Loader2, Search, X, Wallet } from "lucide-react";

import MisExpenseDetail from "./MisExpenseDetail";
import MisAopVariance from "./MisAopVariance";
import MisAnalysisAskBox from "./MisAnalysisAskBox";

export default function MisExpenses() {
  const [props, setProps] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [aop, setAop] = useState(null);
  const [aopLoading, setAopLoading] = useState(false);
  const [aopMonth, setAopMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Load property list — prefer MIS-Analysis dedicated list, fall back to BD MIS
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mis-analysis/properties");
        if (r.data?.properties?.length) {
          setProps(r.data.properties);
          return;
        }
      } catch (e) { logError("MIS-Analysis properties failed, falling back", e); }
      try {
        const { data: bd } = await api.get("/property-ic/mis-properties");
        setProps(Array.isArray(bd) ? bd : (bd?.properties || []));
      } catch (e) { logError("loadProps failed", e); }
    })();
  }, []);

  // Pre-select via ?location=...
  useEffect(() => {
    const loc = searchParams.get("location");
    if (loc && loc !== selected) setSelected(loc);
  }, [searchParams, selected]);

  // Fetch property expense detail + AOP plan (best-effort)
  useEffect(() => {
    if (!selected) { setData(null); setAop(null); return; }
    let cancelled = false;
    setLoading(true); setAopLoading(true);
    (async () => {
      try {
        const r = await api.get(`/mis-analysis/property-expenses/${encodeURIComponent(selected)}?months=12`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        logError("property-expenses failed", e);
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    (async () => {
      try {
        const r = await api.get(`/mis-analysis/aop/${encodeURIComponent(selected)}`);
        if (!cancelled) {
          setAop(r.data);
          const firstFuture = (r.data.months || []).find((m) => !m.has_actual) || r.data.months?.[0];
          setAopMonth(firstFuture?.key || null);
        }
      } catch {
        if (!cancelled) { setAop(null); setAopMonth(null); }
      } finally { if (!cancelled) setAopLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const choose = (loc) => {
    setSelected(loc);
    const next = new URLSearchParams(searchParams);
    if (loc) next.set("location", loc); else next.delete("location");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = props.filter((p) => p.is_operational !== false);
    if (!term) return base.slice(0, 30);
    return base.filter((p) =>
      p.location?.toLowerCase().includes(term) ||
      p.state?.toLowerCase().includes(term) ||
      p.type?.toLowerCase().includes(term),
    ).slice(0, 30);
  }, [q, props]);

  const latest = data?.timeline?.[data.timeline.length - 1];
  const prev = data?.timeline?.[data.timeline.length - 2];

  return (
    <AppShell title="Expense Tracker" subtitle="MIS analysis · per-property line items" persona="mis">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <PropertyPicker
          q={q}
          setQ={setQ}
          filtered={filtered}
          selected={selected}
          onPick={choose}
        />

        <main className="lg:col-span-9 space-y-5">
          {!selected ? (
            <EmptyState />
          ) : loading ? (
            <LoadingState location={selected} />
          ) : !data ? (
            <ErrorState location={selected} />
          ) : (
            <>
              <MisExpenseDetail data={data} latest={latest} prev={prev} />
              {aop && <MisAopVariance aop={aop} aopMonth={aopMonth} setAopMonth={setAopMonth} />}
              {aopLoading && !aop && (
                <div className="text-[11px] text-zinc-400 italic text-center py-2" data-testid="aop-loading">
                  Checking for AOP plan…
                </div>
              )}
              <MisAnalysisAskBox location={data.location} />
            </>
          )}
        </main>
      </div>
    </AppShell>
  );
}

function PropertyPicker({ q, setQ, filtered, selected, onPick }) {
  return (
    <aside className="lg:col-span-3 bg-white border border-zinc-200 rounded-xl p-4 self-start" data-testid="mis-expense-picker">
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5">Property</div>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-teal-600" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search property…"
          className="pl-7 h-8 rounded-md border-teal-200 text-[12px]"
          data-testid="mis-expense-search"
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-2 top-2 text-teal-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <ul className="mt-3 space-y-1 max-h-[68vh] overflow-y-auto">
        {filtered.map((p) => {
          const active = p.location === selected;
          return (
            <li key={p.location}>
              <button
                onClick={() => onPick(p.location)}
                data-testid={`mis-expense-pick-${p.location}`}
                className={`w-full text-left px-2.5 py-1.5 rounded text-[12px] border transition-colors ${
                  active ? "bg-teal-50 border-teal-300 text-teal-900" : "border-transparent hover:bg-zinc-50 text-zinc-700"
                }`}
              >
                <div className="font-medium truncate">{p.location}</div>
                <div className="text-[10px] text-zinc-500 truncate">{p.type} · {p.beds || "—"} beds</div>
              </button>
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="text-[11px] text-zinc-400 py-3 text-center">No properties match "{q}"</li>
        )}
      </ul>
    </aside>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center" data-testid="mis-expense-empty">
      <Wallet className="w-8 h-8 text-teal-600 mx-auto mb-3" />
      <div className="font-display font-black text-xl text-zinc-800">Pick a property</div>
      <p className="text-[12px] text-zinc-500 mt-1">Drill into 12 months of expense line items, MoM deltas and outlier flags.</p>
    </div>
  );
}

function LoadingState({ location }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center text-zinc-500" data-testid="mis-expense-loading">
      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Crunching {location}…
    </div>
  );
}

function ErrorState({ location }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-12 text-center text-rose-600" data-testid="mis-expense-error">
      No MIS history found for "{location}".
    </div>
  );
}
